import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { Fade, Grid, Button, Chip, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getDataFull, deleteData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PersonIcon from '@mui/icons-material/Person';
import { useSnackbar } from 'notistack';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import { blue, grey, red } from '@mui/material/colors';
import ListadoOperativoBanner from '../../components/listados/ListadoOperativoBanner';
import ProcessGuide from '../../components/orden/ProcessGuide';

const CHIP_DEFS = [
    { key: 'todos', label: 'Todos', color: 'primary' },
    { key: 'con_celular', label: 'Con celular', color: 'success' },
    { key: 'con_email', label: 'Con correo', color: 'info' },
    { key: 'sin_contacto', label: 'Sin contacto', color: 'warning' },
];

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [selectedChip, setSelectedChip] = useState('todos');
    const [counts, setCounts] = useState({});
    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const chipItems = useMemo(
        () => CHIP_DEFS.map((c) => ({ ...c, count: counts[c.key] != null ? counts[c.key] : 0 })),
        [counts]
    );

    const onChipChange = useCallback((key) => {
        setSelectedChip(key);
        setTableMainNeedUpdate(Date.now());
    }, []);

    const onNuevo = useCallback(() => {
        globalsContext.setIdEdit(false);
        navigate('/paciente/nuevo');
    }, [globalsContext, navigate]);

    const onVer = useCallback((row) => {
        globalsContext.setIdEdit(row.id);
        navigate('/paciente/ver');
    }, [globalsContext, navigate]);

    const onEditar = useCallback((row) => {
        globalsContext.setIdEdit(row.id);
        navigate('/paciente/editar');
    }, [globalsContext, navigate]);

    const onEliminar = useCallback((row) => {
        alertModal.showModal(
            'Eliminar paciente',
            'El paciente "' + (row.nombre || '') + '" será eliminado. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const response = await deleteData(`pacientes/${row.id}`, usuario.token);
                if (response.error === '') {
                    enqueueSnackbar('Paciente eliminado correctamente', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
                loadingModal.setOpen(false);
            }
        );
    }, [usuario.token, alertModal, loadingModal, enqueueSnackbar]);

    const tableMain = useMemo(() => ({
        title: 'Pacientes',
        idName: 'id',
        defaultOrder: 'asc',
        headers: [
            { id: 'nombre', label: 'Nombre', dataType: DataType.TEXT, sortable: true },
            { id: 'dni', label: 'DNI', dataType: DataType.TEXT, sortable: true },
            { id: 'fecha_nacimiento', label: 'Nacimiento', dataType: DataType.DATE, sortable: true },
            {
                id: 'celular',
                label: 'Celular',
                dataType: DataType.TEXT,
                sortable: false,
                customRender: (row) => row.celular ? (
                    <Link href={`tel:${row.celular}`} underline="hover" onClick={(e) => e.stopPropagation()}>
                        {row.celular}
                    </Link>
                ) : <Chip size="small" label="Sin dato" variant="outlined" />,
            },
            {
                id: 'email',
                label: 'Correo',
                dataType: DataType.TEXT,
                sortable: false,
                customRender: (row) => row.email ? (
                    <Link href={`mailto:${row.email}`} underline="hover" onClick={(e) => e.stopPropagation()}>
                        {row.email}
                    </Link>
                ) : <Chip size="small" label="Sin dato" variant="outlined" />,
            },
            { id: 'localidad_nombre', label: 'Localidad', dataType: DataType.TEXT, sortable: false },
            { id: 'texto_display', label: 'Domicilio', dataType: DataType.TEXT, sortable: false },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, _sf, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);
            const params = new URLSearchParams();
            params.append('per_page', String(rowsPerPage));
            params.append('page', String(page + 1));
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);
            if (selectedChip && selectedChip !== 'todos') params.append('chip', selectedChip);

            const response = await getDataFull(`pacientes?${params.toString()}`, usuario.token);
            if (response.error === '') {
                pageUpdate(response.meta?.page ? response.meta.page - 1 : page);
                totalUpdate(response.meta?.total || 0);
                if (filterListUpdate) filterListUpdate([]);
                if (response.counts) setCounts(response.counts);
                setRowsTableMain(response.data || []);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        },
        moreMenuItems: [],
        rowActions: (row) => [
            {
                label: 'Ver',
                secondary: 'Ficha e historial',
                icon: <VisibilityRoundedIcon sx={{ color: grey[700] }} />,
                itemColor: grey[700],
                action: () => onVer(row),
            },
            {
                label: 'Editar',
                secondary: 'Modificar datos y contacto',
                icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                itemColor: blue[600],
                action: () => onEditar(row),
            },
            {
                label: 'Eliminar',
                secondary: 'Eliminar paciente',
                icon: <DeleteRoundedIcon sx={{ color: red[600] }} />,
                itemColor: red[600],
                dividerBefore: true,
                action: () => onEliminar(row),
            },
        ],
    }), [usuario.token, selectedChip, onVer, onEditar, onEliminar, enqueueSnackbar]);

    return (
        <Layout nombrePagina="Pacientes">
            <Fade in timeout={900}>
                <Grid size={12}>
                    <ListadoOperativoBanner
                        title="Pacientes"
                        description="Registra y consulta pacientes. El celular o correo permiten avisar el turno asignado."
                        icon={PersonIcon}
                        accentColor="primary"
                        headerAction={(
                            <Button startIcon={<AddCircleOutlineRoundedIcon />} onClick={onNuevo}>
                                Nuevo paciente
                            </Button>
                        )}
                        chips={chipItems}
                        selectedChip={selectedChip}
                        onChipChange={onChipChange}
                    />
                    <ProcessGuide
                        title="Guía rápida"
                        steps={[
                            'Indique al menos un medio de contacto (celular o correo) al cargar el paciente.',
                            'Use los filtros para revisar contactos incompletos.',
                            'Desde la ficha se pueden ver las órdenes asociadas.',
                        ]}
                    />
                    <MaterialTable
                        title={tableMain.title}
                        loadData={tableMain.loadData}
                        headers={tableMain.headers}
                        data={rowsTableMain}
                        moreMenuItems={tableMain.moreMenuItems}
                        selectable={false}
                        rowActions={tableMain.rowActions}
                        idName={tableMain.idName}
                        defaultOrder={tableMain.defaultOrder}
                        defaultRowsPerPage={10}
                        needUpdate={tableMainNeedUpdate}
                        hideFilters
                        onRowClick={(row) => onVer(row)}
                    />
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
